import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import Loader from 'components/Loader';
import { Switch } from 'components/ui/switch';
import {
  useGetUserPreference,
  usePostUserPreference,
  useUpdateUserPreference,
} from 'services/settings/hooks';
import { UserPreference } from 'services/settings/type';
import useAuthStore from 'store/auth';
import Icon from 'components/icon/Icon';
import VerifyWhatsappDialog from 'components/dialogs/VerifyWhatsappDialog';

export default function CommunicationPreferencePage() {
  const { t } = useTranslation();
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [preferenceData, setPreferenceData] = useState<
    Omit<UserPreference, 'preference_id'>
  >({
    email_flag: false,
    whatsapp_flag: false,
    push_flag: false,
  });
  const [openWhatsapp, setOpenWhatsapp] = useState<boolean>(false);

  const {
    isFetching,
    data: userPreferences,
    refetch,
  } = useGetUserPreference({
    profileId,
  });
  const { mutateAsync: postPreference, isPending: isPostPending } =
    usePostUserPreference({
      onError: (err) => {
        if (err instanceof AxiosError) {
          const statusCode = err.response?.status;

          if (statusCode === 400) {
            setOpenWhatsapp(true);
          }
        }
      },
    });
  const { mutateAsync: updatePreference, isPending: isUpdatePending } =
    useUpdateUserPreference({
      onError: (err) => {
        if (err instanceof AxiosError) {
          const statusCode = err.response?.status;

          if (statusCode === 400) {
            setOpenWhatsapp(true);
          }
        }
      },
    });

  useEffect(() => {
    if (userPreferences) {
      const { email_flag, whatsapp_flag, push_flag } = userPreferences;
      setPreferenceData({ email_flag, whatsapp_flag, push_flag });
    }
  }, [userPreferences]);

  const onCommunicationToggle = async (checked: boolean, flag: string) => {
    try {
      const updatedPreference: UserPreference = {
        ...preferenceData,
        [flag]: checked,
      };
      if (userPreferences?.preference_id) {
        await updatePreference({
          ...updatedPreference,
          preference_id: userPreferences.preference_id,
        });
      } else {
        await postPreference(updatedPreference);
      }
      setPreferenceData(updatedPreference);
      toast.success(t('communication_updated_msg'));
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const preferences = useMemo(
    () => [
      {
        channel: t('email'),
        desc: t('email_preference_desc'),
        flag: 'email_flag',
        disabled: true,
        icon: 'mail',
      },
      {
        channel: t('whatsapp'),
        desc: t('whatsapp_preference_desc'),
        flag: 'whatsapp_flag',
        disabled: false,
        icon: 'whatsapp',
      },
      {
        channel: t('notifications'),
        desc: t('notifications_preference_desc'),
        flag: 'push_flag',
        disabled: false,
        icon: 'bell',
      },
    ],
    [userPreferences, preferenceData]
  );

  if (isFetching || isPostPending || isUpdatePending) {
    return <Loader />;
  }

  const onPreferenceUpdate = () => {
    setPreferenceData((prev) => ({ ...prev, whatsapp_flag: true }));
    refetch();
  };

  return (
    <>
      <div className='px-4 py-12'>
        <div className='flex flex-col max-w-xl gap-4 mx-auto'>
          {preferences.map(({ channel, flag, desc, disabled, icon }) => {
            return (
              <div
                className='flex items-center justify-between p-4 border rounded-md border-secondary gap-x-4'
                key={channel}
              >
                <div className='flex items-start'>
                  <Icon
                    icon={icon}
                    size={22}
                    className='mt-[1px] sm:mt-1 mr-2 text-secondary'
                  />
                  <div>
                    <h2 className='text-base font-semibold leading-tight sm:text-lg'>
                      {channel}
                    </h2>
                    <p className='text-sm leading-tight sm:text-base'>{desc}</p>
                  </div>
                </div>

                <Switch
                  checked={
                    preferenceData?.[flag as keyof typeof preferenceData] ||
                    false
                  }
                  onCheckedChange={(checked: boolean) =>
                    onCommunicationToggle(checked, flag)
                  }
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      </div>

      <VerifyWhatsappDialog
        open={openWhatsapp}
        onClose={() => setOpenWhatsapp(false)}
        preferenceData={{ ...preferenceData, whatsapp_flag: true }}
        onPreferenceUpdate={onPreferenceUpdate}
      />
    </>
  );
}
